<!--
  The persistent player bar. V1 had one of these and it was genuinely good, so V2 keeps
  it: an operator can hear what listeners hear without leaving the page.

  The stream URL comes from the API's stream descriptors. Nothing here hardcodes a
  mount, which is what lets a codec or bitrate change happen without a release.
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ShioTimecode, ICONS } from '@shiocast/ui';
import { useStationStore } from '@/stores/station';

const { t } = useI18n();
const station = useStationStore();
const audio = ref<HTMLAudioElement | null>(null);
const playing = ref(false);
const muted = ref(true);

const stream = computed(() => {
  const streams = station.nowPlaying?.streams ?? [];
  return streams.find((s) => s.is_default) ?? streams[0] ?? null;
});

/**
 * The descriptor URL is absolute by contract, but the guard stays: a relative or empty
 * URL would make the audio element request the panel's own origin, and a monitor button
 * that plays the wrong thing is worse than one that is disabled.
 */
const canMonitor = computed(() => {
  const url = stream.value?.url;
  return typeof url === 'string' && /^https?:\/\//i.test(url);
});

const title = computed(() => station.nowPlaying?.on_air?.title ?? t('player.nothingOnAir'));
const artwork = computed(() => station.nowPlaying?.on_air?.artwork_url ?? null);
const listeners = computed(() => station.nowPlaying?.listeners.current ?? null);

/**
 * How far through the current item we are.
 *
 * Drawn as a hairline across the full width above the bar, as V1 had it. It is the one
 * piece of on-air state visible from every page in the product, which is why it sits
 * outside the bar's padding rather than inside a card.
 */
const progress = computed(() => {
  const onAir = station.nowPlaying?.on_air;
  if (!onAir) return 0;
  const total = onAir.elapsed_ms + onAir.remaining_ms;
  return total > 0 ? Math.min(100, (onAir.elapsed_ms / total) * 100) : 0;
});

/** What is carrying the audio: the rotation, a live studio, or the emergency source. */
const sourceLabel = computed(() => {
  switch (station.mode) {
    case 'LIVE':
      return station.nowPlaying?.on_air?.live_streamer?.display_name ?? t('player.live');
    case 'FALLBACK':
      return t('player.fallback');
    case 'OFFLINE':
      return t('player.offline');
    default:
      return t('player.auto');
  }
});
const subtitle = computed(
  () => station.nowPlaying?.on_air?.show?.name ?? station.nowPlaying?.on_air?.artist ?? '',
);

function toggle(): void {
  const element = audio.value;
  if (!element) return;
  if (playing.value) {
    element.pause();
    playing.value = false;
    return;
  }
  // Monitoring should not surprise a studio. The player starts muted and the operator
  // unmutes deliberately.
  void element.play().then(
    () => {
      playing.value = true;
    },
    () => {
      playing.value = false;
    },
  );
}

watch(muted, (value) => {
  if (audio.value) audio.value.muted = value;
});
</script>

<template>
  <div class="shio-player">
    <!--
      The progress hairline, full width. Not decoration: it is the only indication of how
      far through the current item the station is that survives on every page.
    -->
    <div
      class="shio-player__progress"
      role="progressbar"
      :aria-valuenow="Math.round(progress)"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="t('player.progress')"
    >
      <span class="shio-player__progress-fill" :style="{ width: `${progress}%` }"></span>
    </div>

    <div class="shio-player__art" aria-hidden="true">
      <img v-if="artwork" :src="artwork" alt="" />
      <component :is="ICONS.Radio" v-else :size="18" />
    </div>

    <div class="shio-player__controls">
      <button
        class="shio-btn shio-btn--secondary shio-btn--icon"
        type="button"
        :disabled="!canMonitor"
        :aria-label="playing ? t('player.stopMonitoring') : t('player.monitor')"
        @click="toggle"
      >
        <component :is="playing ? ICONS.Pause : ICONS.Play" :size="16" />
      </button>
      <button
        class="shio-btn shio-btn--ghost shio-btn--icon"
        type="button"
        :aria-label="muted ? t('player.unmute') : t('player.mute')"
        :aria-pressed="muted"
        @click="muted = !muted"
      >
        <component :is="muted ? ICONS.VolumeX : ICONS.Volume2" :size="16" />
      </button>
    </div>

    <div class="shio-player__text">
      <span class="shio-player__title">{{ title }}</span>
      <span v-if="subtitle" class="shio-meta">{{ subtitle }}</span>
    </div>

    <div class="shio-player__chips">
      <span class="shio-player__chip" :class="`shio-player__chip--${station.mode.toLowerCase()}`">
        {{ sourceLabel }}
      </span>
      <span v-if="listeners !== null" class="shio-player__chip">
        <component :is="ICONS.Users" :size="12" aria-hidden="true" />
        {{ listeners }}
        <span class="shio-sr-only">{{ t('player.listeners') }}</span>
      </span>
    </div>

    <div v-if="station.nowPlaying?.on_air" class="shio-player__times">
      <ShioTimecode :ms="station.nowPlaying.on_air.elapsed_ms" :label="t('onAir.elapsed')" />
      <span class="shio-meta" aria-hidden="true">/</span>
      <ShioTimecode :ms="station.nowPlaying.on_air.remaining_ms" :label="t('onAir.remaining')" />
    </div>

    <span v-if="stream" class="shio-meta shio-player__stream">
      {{ stream.codec }} {{ stream.bitrate_kbps }}k
    </span>

    <audio v-if="canMonitor && stream" ref="audio" :src="stream.url" muted preload="none" />
  </div>
</template>

<style scoped>
.shio-player {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--shio-space-3);
  padding: 0 var(--shio-space-4);
  height: 100%;
}

.shio-player__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: hsl(var(--shio-border));
}

.shio-player__progress-fill {
  display: block;
  height: 100%;
  background: hsl(var(--shio-accent-primary));
  box-shadow: 0 0 8px hsl(var(--shio-accent-primary) / 0.6);
  transition: width var(--shio-motion-panel) linear;
}

.shio-player__art {
  display: grid;
  place-items: center;
  flex: none;
  width: 44px;
  height: 44px;
  overflow: hidden;
  border: 1px solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-sm);
  background: hsl(var(--shio-surface-base));
  color: hsl(var(--shio-text-muted));
}

.shio-player__art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shio-player__chips {
  display: flex;
  gap: var(--shio-space-1);
}

.shio-player__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px var(--shio-space-2);
  border: 1px solid hsl(var(--shio-border));
  border-radius: 999px;
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-badge-size);
  color: hsl(var(--shio-text-secondary));
  white-space: nowrap;
}

/* The source chip is the one thing here that is never only colour: it always names what
   is carrying the audio. */
.shio-player__chip--live {
  border-color: hsl(var(--shio-status-live) / 0.6);
  color: hsl(var(--shio-status-live));
}

.shio-player__chip--fallback {
  border-color: hsl(var(--shio-status-fallback) / 0.6);
  color: hsl(var(--shio-status-fallback));
}

.shio-player__chip--offline {
  border-color: hsl(var(--shio-status-offline) / 0.6);
  color: hsl(var(--shio-text-muted));
}

.shio-player__chip--auto {
  border-color: hsl(var(--shio-source-auto) / 0.5);
  color: hsl(var(--shio-source-auto));
}

.shio-player__controls {
  display: flex;
  gap: var(--shio-space-1);
}

.shio-player__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.shio-player__title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shio-player__times {
  display: flex;
  align-items: center;
  gap: var(--shio-space-2);
}

/* On a narrow bar the format and the elapsed times give way. The source chip and the
   listener count stay: what is carrying the audio is never hidden. */
@media (max-width: 720px) {
  .shio-player__stream,
  .shio-player__times {
    display: none;
  }
}

@media (max-width: 520px) {
  .shio-player__art {
    display: none;
  }
}
</style>
